import {
  ChangeEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  deleteDocuments,
  getAllDocuments,
  updateDocument,
} from "@services/api";
import { deleteReading } from "@features/workspace-readings/state/workspaceReadingsSlice";
import AddToWorkspaceMenu, { OwnedWorkspace } from "./AddToWorkspaceMenu";

interface WorkspaceLink {
  workspaceId: string;
  workspaceName: string;
  readingId: string;
}

interface FileCard {
  documentId: string;
  documentName: string;
  ownerName: string;
  documentUrl: string | null;
  updatedAt: string | null;
  workspaces: WorkspaceLink[];
}

interface Props {
  refreshKey?: number;
  ownedWorkspaces?: OwnedWorkspace[];
  onRequestRefresh?: () => void;
}

// Compact card-based "grid" view of /files. Lives alongside the existing
// DocumentsTable so the user can toggle between list and grid. Both views
// expose the same actions (rename, unlink-from-current-workspace, delete).
// Thumbnails are deliberately icon-only here — real PDF.js first-page
// previews are tracked as a follow-up since they require a worker config,
// per-card lazy loading, and a thumbnail cache.
export default function FilesGrid({
  refreshKey,
  ownedWorkspaces = [],
  onRequestRefresh,
}: Props) {
  const dispatch = useAppDispatch();
  const userId = useAppSelector((s) => s.user.userId);

  const [files, setFiles] = useState<FileCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTargetId, setMenuTargetId] = useState<string | null>(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [addMenuTargetId, setAddMenuTargetId] = useState<string | null>(null);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [unlinkingReadingId, setUnlinkingReadingId] = useState<string | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Multi-select state. selected[] holds document_ids of marked cards.
  // Selection mode is "active" whenever the array is non-empty; in that
  // mode all checkboxes stay visible and a plain card-body click toggles
  // selection instead of opening the file. Esc clears.
  const [selected, setSelected] = useState<string[]>([]);
  const selectionActive = selected.length > 0;

  const toggleSelect = (documentId: string) => {
    setSelected((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    );
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelected([]);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setError("");
    getAllDocuments("")
      .then((result) => {
        setLoading(false);
        if (!result.success) {
          setError(result.error.message);
          return;
        }
        setFiles(
          result.data.map((item) => ({
            documentId: item.document_id,
            documentName: item.document_name,
            ownerName: item.owner_username || "Unknown",
            documentUrl: item.document_url,
            updatedAt: item.updated_at,
            workspaces: (item.workspaces ?? []).map((w) => ({
              workspaceId: w.workspace_id,
              workspaceName: w.workspace_name,
              readingId: w.reading_id,
            })),
          }))
        );
      })
      .catch((err) => {
        setLoading(false);
        setError(err instanceof Error ? err.message : "Failed to load files");
      });
  }, [userId, refreshKey]);

  const target = useMemo(
    () => files.find((f) => f.documentId === menuTargetId) ?? null,
    [files, menuTargetId]
  );

  const openMenu = (e: MouseEvent<HTMLElement>, documentId: string) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuTargetId(documentId);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuTargetId(null);
  };

  const startEdit = (card: FileCard) => {
    setEditingId(card.documentId);
    setEditValue(card.documentName);
    closeMenu();
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };
  const commitEdit = async (card: FileCard) => {
    const next = editValue.trim();
    if (!next || next === card.documentName) {
      cancelEdit();
      return;
    }
    setSavingId(card.documentId);
    const result = await updateDocument(card.documentId, next, card.documentUrl);
    setSavingId(null);
    cancelEdit();
    if (!result.success) {
      setError(result.error.message);
      return;
    }
    setFiles((prev) =>
      prev.map((f) =>
        f.documentId === card.documentId ? { ...f, documentName: next } : f
      )
    );
  };

  const handleUnlinkLink = async (
    card: FileCard,
    link: WorkspaceLink
  ) => {
    if (
      !window.confirm(
        `Remove "${card.documentName}" from "${link.workspaceName}"? The file stays in your library; only the reading and its annotations are deleted.`
      )
    ) {
      return;
    }
    setUnlinkingReadingId(link.readingId);
    try {
      await dispatch(deleteReading(link.readingId)).unwrap();
      setFiles((prev) =>
        prev.map((f) =>
          f.documentId === card.documentId
            ? {
                ...f,
                workspaces: f.workspaces.filter(
                  (w) => w.readingId !== link.readingId
                ),
              }
            : f
        )
      );
    } catch (err) {
      console.error("Unlink failed:", err);
      setError("Failed to remove from workspace.");
    } finally {
      setUnlinkingReadingId(null);
    }
  };

  const handleDelete = async (card: FileCard) => {
    closeMenu();
    if (
      !window.confirm(
        `Delete "${card.documentName}"? This action cannot be undone. Any readings that reference this file will also be deleted.`
      )
    ) {
      return;
    }
    setDeletingId(card.documentId);
    const result = await deleteDocuments([card.documentId]);
    setDeletingId(null);
    if (!result.success) {
      setError(result.error.message);
      return;
    }
    setFiles((prev) => prev.filter((f) => f.documentId !== card.documentId));
    setSelected((prev) => prev.filter((id) => id !== card.documentId));
  };

  const handleBulkDelete = async () => {
    const targets = files.filter((f) => selected.includes(f.documentId));
    if (targets.length === 0) return;
    if (
      !window.confirm(
        `Delete ${targets.length} file${
          targets.length === 1 ? "" : "s"
        }? This action cannot be undone. Any readings that reference these files will also be deleted.`
      )
    ) {
      return;
    }
    const ids = targets.map((t) => t.documentId);
    const result = await deleteDocuments(ids);
    if (!result.success) {
      setError(result.error.message);
      return;
    }
    setFiles((prev) => prev.filter((f) => !ids.includes(f.documentId)));
    setSelected([]);
  };

  if (loading && files.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (files.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <PictureAsPdfOutlinedIcon
          sx={{ fontSize: 64, color: "text.disabled", mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary">
          No files yet. Use Upload to add one.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {selectionActive && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(144, 202, 249, 0.08)"
                : "rgba(25, 118, 210, 0.06)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
            {selected.length} selected
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={ownedWorkspaces.length === 0}
              onClick={(e) => {
                setAddMenuAnchor(e.currentTarget);
                setBulkAddOpen(true);
              }}
            >
              Add to workspace…
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
            >
              Delete
            </Button>
            <Button size="small" onClick={() => setSelected([])}>
              Clear
            </Button>
          </Stack>
        </Box>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 2,
        }}
      >
        {files.map((card) => {
          const isEditing = editingId === card.documentId;
          const isSaving = savingId === card.documentId;
          const isDeleting = deletingId === card.documentId;
          const isSelected = selected.includes(card.documentId);
          return (
            <Card
              key={card.documentId}
              variant="outlined"
              sx={{
                position: "relative",
                // Reveal the checkbox on hover. When selection mode is
                // active or this card is selected we keep it solid via
                // the inline opacity on the wrapper.
                "&:hover .file-card-checkbox": { opacity: 1 },
                ...(isSelected && {
                  borderColor: "primary.main",
                  borderWidth: 2,
                }),
              }}
            >
              <Box
                className="file-card-checkbox"
                sx={{
                  position: "absolute",
                  top: 4,
                  left: 4,
                  zIndex: 2,
                  bgcolor: "background.paper",
                  borderRadius: "50%",
                  opacity: isSelected || selectionActive ? 1 : 0,
                  transition: "opacity 0.15s",
                }}
              >
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onClick={(e) => {
                    // Stop the click from bubbling to the action area
                    // so we don't open the file or double-toggle.
                    e.stopPropagation();
                    toggleSelect(card.documentId);
                  }}
                />
              </Box>
              <CardActionArea
                disabled={isEditing}
                onClick={(e) => {
                  // Cmd/Ctrl-click always toggles selection — familiar
                  // Finder/Explorer behavior. Otherwise, in selection
                  // mode the body click toggles too (so users don't
                  // accidentally open a file mid-select). When no
                  // selection is active, click opens the file.
                  if (e.metaKey || e.ctrlKey || selectionActive) {
                    toggleSelect(card.documentId);
                    return;
                  }
                  if (card.documentUrl) {
                    window.open(
                      card.documentUrl,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }
                }}
              >
                <Box
                  sx={{
                    height: 140,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "action.hover",
                  }}
                >
                  <PictureAsPdfOutlinedIcon
                    sx={{ fontSize: 64, color: "text.disabled" }}
                  />
                </Box>
              </CardActionArea>
              <CardContent
                sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editValue}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setEditValue(e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void commitEdit(card);
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      onBlur={() => commitEdit(card)}
                      autoFocus
                      disabled={isSaving}
                      inputProps={{ maxLength: 255 }}
                      fullWidth
                    />
                  ) : (
                    <Tooltip title={card.documentName}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                        sx={{ flex: 1 }}
                      >
                        {card.documentName}
                      </Typography>
                    </Tooltip>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => openMenu(e, card.documentId)}
                    disabled={isEditing || isSaving || isDeleting}
                  >
                    {isDeleting ? (
                      <CircularProgress size={16} />
                    ) : (
                      <MoreVertIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {card.ownerName}
                </Typography>
                {card.workspaces.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {card.workspaces.map((link) => (
                      <Chip
                        key={link.readingId}
                        label={link.workspaceName}
                        size="small"
                        variant="outlined"
                        onDelete={() => handleUnlinkLink(card, link)}
                        disabled={
                          unlinkingReadingId === link.readingId
                        }
                      />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => target && startEdit(target)}
          disabled={!target}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Rename
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            // Re-anchor on the row's overflow button so the picker
            // pops next to where the user invoked it.
            setAddMenuAnchor(menuAnchor);
            setAddMenuTargetId(menuTargetId);
            closeMenu();
            void e;
          }}
          disabled={!target || ownedWorkspaces.length === 0}
        >
          <AddIcon fontSize="small" sx={{ mr: 1 }} />
          Add to workspace…
        </MenuItem>
        <MenuItem
          onClick={() => target && handleDelete(target)}
          disabled={!target}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <AddToWorkspaceMenu
        anchorEl={addMenuAnchor}
        targets={(() => {
          if (bulkAddOpen) {
            return files
              .filter((f) => selected.includes(f.documentId))
              .map((f) => ({
                documentId: f.documentId,
                documentName: f.documentName,
                linkedWorkspaceIds: new Set(
                  f.workspaces.map((w) => w.workspaceId)
                ),
              }));
          }
          const card = files.find((f) => f.documentId === addMenuTargetId);
          if (!card) return [];
          return [
            {
              documentId: card.documentId,
              documentName: card.documentName,
              linkedWorkspaceIds: new Set(
                card.workspaces.map((w) => w.workspaceId)
              ),
            },
          ];
        })()}
        ownedWorkspaces={ownedWorkspaces}
        onClose={() => {
          setAddMenuAnchor(null);
          setAddMenuTargetId(null);
          setBulkAddOpen(false);
        }}
        onAdded={() => onRequestRefresh?.()}
        onError={(msg) => setError(msg)}
      />
    </Box>
  );
}
